import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, BookText as TikTok, XCircle } from 'lucide-react';

interface FooterProps {
  language: string;
}

const Footer: React.FC<FooterProps> = ({ language }) => {
  const [showBusinessConditions, setShowBusinessConditions] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  const content = {
    en: {
      title: "BeautyEsthetics",
      description: "Your premier destination for premium beauty treatments. We specialize in PRP, Botox, Filler, and Mesotherapy, helping you enhance your natural beauty.",
      quickLinks: {
        title: "Quick Links",
        services: "Services",
        about: "About Us",
        testimonials: "Testimonials",
        contact: "Contact",
        conditions: "Business Conditions",
        privacy: "Cookie & Privacy Policy"
      },
      contact: {
        title: "Contact Us",
        address: "Address",
        phone: "Phone",
        email: "Email",
        visit: {
          title: "Visit Us",
          description: "We're located in Aarhus.\nEasy access via public transportation."
        }
      },
      rights: "All rights reserved"
    },
    da: {
      title: "BeautyEsthetics",
      description: "Din førende destination for premium skønhedsbehandlinger. Vi specialiserer os i PRP, Botox, Filler og Mesoterapi og hjælper dig med at fremhæve din naturlige skønhed.",
      quickLinks: {
        title: "Hurtige links",
        services: "Behandlinger",
        about: "Om os",
        testimonials: "Anmeldelser",
        contact: "Kontakt",
        conditions: "Handelsbetingelser",
        privacy: "Cookie- og privatlivspolitik"
      },
      contact: {
        title: "Kontakt os",
        address: "Adresse",
        phone: "Telefon",
        email: "Email",
        visit: {
          title: "Besøg os",
          description: "Vi ligger i Aarhus.\nNem adgang med offentlig transport."
        }
      },
      rights: "Alle rettigheder forbeholdes"
    }
  };

  return (
    <footer className="bg-[#1A1A1A] text-white relative">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Clinic Info */}
          <div>
            <h3 
              className="text-xl font-serif font-semibold mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {content[language as keyof typeof content].title}
            </h3>
            <p className="mb-4 text-sm leading-relaxed text-gray-300">
              {content[language as keyof typeof content].description}
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.instagram.com/_beauty_esthetic_docaba?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-[#A68B7C] transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://www.tiktok.com/@beautyesthetics.dk?is_from_webapp=1&sender_device=pc" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-[#A68B7C] transition-colors"
                aria-label="TikTok"
              >
                <TikTok size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 
              className="text-xl font-serif font-semibold mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {content[language as keyof typeof content].quickLinks.title}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/services" 
                  className="text-gray-300 hover:text-[#A68B7C] transition-colors"
                >
                  {content[language as keyof typeof content].quickLinks.services}
                </Link>
              </li>
              <li>
                <Link 
                  to="/about" 
                  className="text-gray-300 hover:text-[#A68B7C] transition-colors"
                >
                  {content[language as keyof typeof content].quickLinks.about}
                </Link>
              </li>
              <li>
                <Link 
                  to="/testimonials" 
                  className="text-gray-300 hover:text-[#A68B7C] transition-colors"
                >
                  {content[language as keyof typeof content].quickLinks.testimonials}
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="text-gray-300 hover:text-[#A68B7C] transition-colors"
                >
                  {content[language as keyof typeof content].quickLinks.contact}
                </Link>
              </li>
              <li>
                <button 
                  onClick={() => setShowBusinessConditions(true)}
                  className="text-gray-300 hover:text-[#A68B7C] transition-colors"
                >
                  {content[language as keyof typeof content].quickLinks.conditions}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setShowPrivacyPolicy(true)}
                  className="text-gray-300 hover:text-[#A68B7C] transition-colors"
                >
                  {content[language as keyof typeof content].quickLinks.privacy}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3
              className="text-xl font-serif font-semibold mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {content[language as keyof typeof content].contact.title}
            </h3>
            <div className="space-y-4">
              <div className="flex items-start group mb-2">
                <MapPin size={20} className="mr-2 mt-1 flex-shrink-0 text-[#A68B7C] group-hover:scale-110 transition-transform duration-300" />
                <p className="text-gray-300">
                  Thorvaldsensgade 13,<br />
                  8000 Aarhus
                </p>
              </div>
              <div className="flex items-center group mb-1">
                <Phone size={20} className="mr-2 flex-shrink-0 text-[#A68B7C] group-hover:scale-110 transition-transform duration-300" />
                <p className="text-gray-300">+45 42 31 67 54</p>
              </div>
              <div className="flex items-center group">
                <Mail size={20} className="mr-2 flex-shrink-0 text-[#A68B7C] group-hover:scale-110 transition-transform duration-300" />
                <p className="text-gray-300">abaseensle.as@gmail.com</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 text-center">
          <p className="text-sm text-gray-400">
            &copy; {new Date().getFullYear()} BeautyEsthetics. {content[language as keyof typeof content].rights}. CVR: 43415549
          </p>
        </div>
      </div>

      {/* Business Conditions Modal */}
      {showBusinessConditions && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 
                  className="text-xl font-serif font-semibold text-gray-900"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {content[language as keyof typeof content].quickLinks.conditions}
                </h3>
                <button 
                  onClick={() => setShowBusinessConditions(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle size={24} />
                </button>
              </div>
              
              <div className="space-y-4 text-gray-600">
                <section>
                  <h4 className="font-serif font-semibold mb-2">Delivery</h4>
                  <p>Once payment has been confirmed, the purchased service can be booked, and the item is thus considered delivered.</p>
                  <p className="mt-2">When purchasing multiple treatments or a package offer with multiple treatments, the entire treatment course is paid in advance. Refunds for unused treatments are possible, even if the treatment course has begun and there are not enough treatments. However, the treatments used will be calculated at full price if a discount has been given on a package offer and the remaining amount will be refunded.</p>
                </section>

                <section>
                  <h4 className="font-serif font-semibold mb-2">Refund</h4>
                  <p>Treatments that have already begun will not be refunded.</p>
                  <p className="mt-2">There is a 14-day right of withdrawal when purchasing treatments and offers. This is 14 days or more than 14 days before the treatment time. There will be a refund if the above is met.</p>
                  <p className="mt-2">Your purchase may be subject to a time limit, meaning it must be used within a certain period of time after purchase. All treatments purchased in advance expire after 12 months, both those purchased in-clinic and online, however, there is a 3 month expiration period for facials.</p>
                  <p className="mt-2">Please note that products purchased in the clinic that are considered used cannot be returned.</p>
                </section>

                <section>
                  <h4 className="font-serif font-semibold mb-2">Cancellation Policy</h4>
                  <p>Any cancellations of treatment appointments or consultations must be made no later than 24 hours before the scheduled time, otherwise a fee of 20% of the price prepaid for one or more booked treatments will be charged.</p>
                </section>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 
                  className="text-xl font-serif font-semibold text-gray-900"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {content[language as keyof typeof content].quickLinks.privacy}
                </h3>
                <button 
                  onClick={() => setShowPrivacyPolicy(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle size={24} />
                </button>
              </div>
              
              <div className="space-y-4 text-gray-600">
                <p>Beauty Esthetics only collects personal data in relevant cases for our activities, and this also applies to our treatments.</p>
                <p>When collecting, processing and using your personal data, we comply with all applicable legal provisions.</p>
                <p>We only retain your personal data for the period that is either legally required or as long as it is relevant for the purpose for which it was collected.</p>

                <section>
                  <h4 className="font-serif font-semibold mb-2">Information Collection</h4>
                  <p>In order to complete your purchase and provide our service, we need to collect certain personal data from you. We may use cookies to improve your experience and to keep track of the contents of your shopping cart on our webshop.</p>
                  <p className="mt-2">This information may include name, email address, mailing address, shipping address (if different from mailing address), phone number, and payment information.</p>
                  <p className="mt-2">Beauty Esthetics collects and processes your personal data when you perform the following actions:</p>
                  <p className="mt-2">Visit our website.</p>
                </section>

                <section>
                  <h4 className="font-serif font-semibold mb-2">The Person Responsible for Data</h4>
                  <p>Responsible for the collection, processing and use of your personal data on www.beautyesthetics.dk is Beauty Esthetics, address: Thorvaldsensgade 13, 8000 Aarhus, CVR: 43415549</p>
                </section>

                <section>
                  <h4 className="font-serif font-semibold mb-2">Basis and Purpose of Processing</h4>
                  <p>We collect your general contact information, such as name and address, in order to deliver the products or services you have purchased from us. We use your email address to send order confirmations and delivery confirmations.</p>
                  <p className="mt-2">Your information (with the exception of your email address, if you have consented to processing for the purpose of receiving offers) will be deleted 2 years after you have received your product or service.</p>
                </section>

                <section>
                  <h4 className="font-serif font-semibold mb-2">Other Recipients of Personal Data</h4>
                  <p>We do not sell your personal data to third parties, and we do not transfer your personal data to countries outside the EU/EEA.</p>
                  <p className="mt-2">Our website is hosted by Simply.com, which acts as our data processor. All personal data you provide to us on our website is stored in Simply.com's data centers.</p>
                  <p className="mt-2">We use external companies as suppliers to ensure the best possible service. These external suppliers are data processors and in some cases process personal data in connection with their provision of services to us. Our data processors only process personal data on our instructions and in accordance with applicable law.</p>
                  <p className="mt-2">We have entered into data processing agreements with our data processors to ensure that they comply with the applicable regulations on the protection of your personal data.</p>
                </section>

                <section>
                  <h4 className="font-serif font-semibold mb-2">Your Rights</h4>
                  <p>As a data subject, you have certain rights that we always ensure are fulfilled. You have the right to request:</p>
                  <ul className="list-disc list-inside mt-2">
                    <li>Access to and correction of your personal data</li>
                    <li>Deletion of personal data</li>
                    <li>Object to the processing of your personal data</li>
                    <li>Complain to a data protection authority</li>
                  </ul>
                  <p className="mt-2">If you no longer want us to process your personal data, or if you wish to restrict the processing of your personal data, you can send us a request via email to abaseensle.as@gmail.com</p>
                </section>
              </div>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;